import React, { useEffect } from 'react';
import moment from 'moment';
import { get, getOr, isEmpty, isNull } from 'lodash/fp';
import { useParams } from 'react-router-dom';
import { useStoreActions, useStoreState } from 'easy-peasy';
import { Box, Button, CircularProgress, Typography } from '@material-ui/core';
import ContinueIcon from '@material-ui/icons/NavigateNext';
import history from '../../../history';
import {
  guestDashboard as guestDashboardPath,
  guestEditProfile as guestProfilePath,
  reservationOrder as reservationOrderPath
} from '../../../paths';
import Layout from '../../../components/Layout';
import PageLoading from '../../../components/PageLoading';
import PantryDetails from '../../../components/PantryDetails';
import StepHeader from '../../../components/ReservationStepHeader';
import ContentBox from '../ContentBox';
import ProgramsSelection from './ProgramsSelection';
import ProgramsOutreaches from './ProgramsOutreaches';
import pagePathToUrl from '../../../helpers/pagePathToUrl';
import { ON_HOLD } from '../../../constants/ReservationStatus';
import STATUS_ACTIONS from '../../../constants/ReservationStatusActions';
import useCurrentUser from '../../../hooks/useCurrentUser';

const eventNotAvailableMessage = 'Selected event is no longer available.';
const pastEventMessage =
  'Selected time slot has already closed. Please select another time slot.';
const unqualifiedGuestForProgramsError =
  "We're sorry, a program has changed, you need to start again.";

const isPastEvent = (event, requestTime) => {
  const { date, endTime, scheduleMinutesInterval } = event;
  const dateTimeToCompare =
    scheduleMinutesInterval === 0 ? `${date} ${endTime}` : requestTime;
  const isPastDate = moment(dateTimeToCompare, 'YYYY-MM-DD LT').isBefore(
    moment()
  );

  return isPastDate;
};

export default function Pantry() {
  const { pantryPath = '' } = useParams();
  const user = useStoreState(get('user.data'));
  const userSession = useCurrentUser();
  const userDetails = useStoreState(get('userDetails.data'));
  const {
    data: reservation,
    programIds: selectedPrograms,
    hasMininumDetails: reservationHasMinDetails,
    hasProgramPicklists: reservationHasProgramPicklists,
    error: reservationError,
    loading: loadingReservation,
    updatingStatus
  } = useStoreState(get('reservation'));

  const {
    data: pantry,
    isExpiredTokenError,
    loading: loadingPantry
  } = useStoreState(get('pantry'));

  const pantryPrograms = useStoreState(get('pantryPrograms.data'));
  const loadingPrograms = useStoreState(get('pantryPrograms.loading'));
  const outreaches = useStoreState(get('programsOutreaches.data'));
  const loadingOutreaches = useStoreState(get('programsOutreaches.loading'));
  const reservedEventsById = useStoreState(state =>
    state.userReservations.reservationsByOutreachIdForPantry(get('id', pantry))
  );
  const {
    event: selectedEvent,
    isUserEligible: isUserElibleForEvent
  } = useStoreState(get('selectedEvent'));
  const getMatchedAndEligibleProgramIdsForOutreach = useStoreState(
    get('programsOutreaches.getMatchedAndEligibleProgramIdsForOutreach')
  );
  const fetchPantry = useStoreActions(get('pantry.fetch'));
  const clearPantry = useStoreActions(get('pantry.clear'));
  const showError = useStoreActions(get('notifications.showError'));
  const setReservation = useStoreActions(get('reservation.set'));
  const createReservation = useStoreActions(get('reservation.create'));

  const clearReservationError = useStoreActions(get('reservation.clearError'));
  const fetchPantryPrograms = useStoreActions(get('pantryPrograms.fetch'));
  const fetchOutreaches = useStoreActions(get('programsOutreaches.fetch'));
  const clearOutreaches = useStoreActions(get('programsOutreaches.clear'));
  const updateStatus = useStoreActions(get('reservation.updateStatus'));
  const clearReservation = useStoreActions(get('reservation.clear'));
  const clearSelectedEvent = useStoreActions(get('selectedEvent.clear'));
  const clearPantryPrograms = useStoreActions(get('pantryPrograms.clear'));
  const fetchUserReservations = useStoreActions(get('userReservations.fetch'));
  const fetchReservationFieldsSettings = useStoreActions(
    get('pantryReservationFieldsSettings.fetch')
  );
  const clearReservationFieldsSettings = useStoreActions(
    get('pantryReservationFieldsSettings.clear')
  );
  const {
    setShowLoginForm,
    setShowSignupForm,
    setShowDialog: showLoginSignupDialog
  } = useStoreActions(get('components.loginSignupDialog'));
  const fetchProgramsPicklists = useStoreActions(
    get('programsPicklists.fetch')
  );

  const isLoggedIn = !isEmpty(userDetails);
  const requestTime = getOr('', 'requestTime', reservation);
  const eventId = get('id', selectedEvent);
  const hasReservationForEvent = get(eventId, reservedEventsById);

  const showContinueButton =
    !isEmpty(outreaches) &&
    !isEmpty(selectedPrograms) &&
    requestTime &&
    !hasReservationForEvent;

  const continueButtonLabel =
    !isLoggedIn || !reservationHasMinDetails || reservationHasProgramPicklists
      ? 'Continue'
      : 'Agree & Confirm Reservation';

  const handleSelectProgram = programIds => {
    clearOutreaches();
    if (!isEmpty(programIds)) {
      fetchOutreaches({ pantryPath, programIds });
      fetchProgramsPicklists({ pantryPath, programIds });
      setReservation({ programIds });
    } else {
      setReservation({ requestTime: '', outreachId: '', programIds });
    }
  };

  const handleContinue = async () => {
    if (!isLoggedIn) {
      showLoginSignupDialog(true);
      setShowLoginForm(true);
      setShowSignupForm(true);
      return;
    }
    if (isPastEvent(selectedEvent, requestTime)) {
      showError({ message: pastEventMessage });
      return;
    }
    if (!isUserElibleForEvent) {
      showError({ message: eventNotAvailableMessage });
      return;
    }

    const { outreachId } = reservation;
    await createReservation({
      pantryPath,
      body: {
        ...reservation,
        programIds: getMatchedAndEligibleProgramIdsForOutreach(outreachId)
      }
    });
  };

  useEffect(() => {
    fetchPantry(pantryPath);
    fetchReservationFieldsSettings(pantryPath);
    return () => {
      clearPantry();
      clearOutreaches();
      clearPantryPrograms();
      clearReservation();
      clearSelectedEvent();
      clearReservationFieldsSettings();
    };
  }, []);

  useEffect(() => {
    if (isExpiredTokenError) {
      // eslint-disable-next-line no-undef
      window.location.reload();
    }
  }, [isExpiredTokenError]);

  useEffect(() => {
    if (pantry) {
      if (pantry.isExpiredClient) {
        showError({ message: `"${pantry.name}" Portal has been disabled.` });
        history.push(guestDashboardPath);
      } else fetchPantryPrograms(pantryPath);
    }
  }, [pantry, showError, fetchPantryPrograms, pantryPath]);

  useEffect(() => {
    const userId = get('id', user);
    if (userId && userSession) {
      fetchUserReservations(userId);
    }
  }, [user]);

  useEffect(() => {
    if (!userDetails) return;
    const { id: userId, email, firstName, lastName, otherInfo } = userDetails;
    setReservation({
      ...otherInfo,
      userId,
      email,
      firstName,
      lastName,
      householdTotal: getOr(1, 'householdTotal', otherInfo)
    });
  }, [userDetails]);

  useEffect(() => {
    const id = get('id', reservation);
    if (!id) return;

    if (!reservationHasMinDetails) {
      history.push(
        `${guestProfilePath}?pantryPath=${pantryPath}&reservationId=${id}`
      );
    } else if (reservationHasProgramPicklists) {
      history.push(pagePathToUrl(reservationOrderPath, { pantryPath, id }));
    } else if (ON_HOLD === reservation.status && !updatingStatus) {
      updateStatus({ id, pantryPath, statusAction: STATUS_ACTIONS.PENDING });
    }
  }, [
    pantryPath,
    reservation,
    updatingStatus,
    reservationHasMinDetails,
    reservationHasProgramPicklists
  ]);

  // Handle reservation creation error
  useEffect(() => {
    const reservationErrorMsg = get('message', reservationError);
    if (reservationErrorMsg) {
      showError({ message: reservationErrorMsg });
      if (reservationErrorMsg === unqualifiedGuestForProgramsError) {
        setReservation({ outreachId: '', requestTime: '', programIds: [] });
        clearOutreaches();
        clearSelectedEvent();
        fetchPantryPrograms(pantryPath);
      }
      clearReservationError();
    }
  }, [showError, reservationError, clearReservationError]);

  useEffect(() => {
    if (requestTime && !isNull(outreaches) && !isUserElibleForEvent) {
      showError({ message: eventNotAvailableMessage });
      setReservation({ outreachId: '', requestTime: '' });
    }
  }, [outreaches, requestTime, reservationError, isUserElibleForEvent]);

  return (
    <Layout>
      {loadingPantry || loadingPrograms ? (
        <PageLoading />
      ) : (
        <>
          {pantry && <PantryDetails pantry={pantry} />}
          <ContentBox>
            <Typography variant="h6">Available Reservation:</Typography>
            <StepHeader label="Step 1: Select Help Desired" />
            <ProgramsSelection
              selected={selectedPrograms}
              disabled={loadingOutreaches}
              programs={pantryPrograms || []}
              onSelect={handleSelectProgram}
            />
            {!isEmpty(selectedPrograms) && (
              <>
                <StepHeader label="Step 2: Select time slot to reserve" />
                <ProgramsOutreaches
                  programIds={selectedPrograms}
                  reservedEventsById={reservedEventsById}
                />
              </>
            )}
            {showContinueButton && (
              <Box textAlign="right">
                <Button
                  style={{ marginLeft: 10 }}
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={handleContinue}
                  endIcon={<ContinueIcon />}
                  disabled={loadingReservation || updatingStatus}
                >
                  {loadingReservation || updatingStatus ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    continueButtonLabel
                  )}
                </Button>
              </Box>
            )}
          </ContentBox>
        </>
      )}
    </Layout>
  );
}
